import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';

export interface MigrationMappableDriverAtTarget {
  driverId: string;
  driverName: string;
  driverPhone: string;
  fromRegionId: number;
  milesFromOpportunity: number;
  lng: number;
  lat: number;
}

export interface MigrationMappableTarget {
  name: string;
  description: string;
  regionId: number;
  lng: number;
  lat: number;
  sandDemandSnapshotId: number;
  driversDesired: number;
  startDate: {
    year: number;
    month: number;
    day: number;
  };
  durationDays: number;
  estimatedCentsPerShift: number;
  experienceNames: string[];
  drivers: Array<MigrationMappableDriverAtTarget>;
  trailerTypeNames: string[];
  closestDriverMiles: number;
  regionDrivers: Array<RegionDriver>;
  maxShiftCentsDifference: number;
  loadBonus?: Bonus;
}

export interface RegionDriver {
  driverId: string;
  driverName: string;
  driverPhone: string;
  fromRegionId: number;
  milesFromOpportunity: number;
  lng: number;
  lat: number;
}

export interface MigrationMappableTargetAccepted {
  name: string;
  regionId: number;
  lng: number;
  lat: number;
  sandDemandSnapshotId: number;
  estimatedCentsPerShift: number;
  acceptedDrivers: Array<MigrationMappableDriverAccepted>;
}

export interface MigrationMappableDriverAccepted {
  driverId: string;
  driverName: string;
  driverPhone: string;
  fromRegionId: number;
  milesFromOpportunity: number;
  lng: number;
  lat: number;
}

export interface MigrationMappableDriver {
  userId: string;
  name: string;
  truckName: string;
  trailerName: string;
  phone: string;
  lastWaypointLnglat: {
    longitude: number;
    latitude: number;
  };
  lastWaypointTime: string;
  loadId: string;
  onShift: boolean;
  onHitch: boolean;
  onTour: boolean;
}

export interface DriverId {
  driverId: string;
}

export interface SandId {
  sandDemandSnapshotId: number;
}

export interface DriversForTarget {
  sandDemandSnapshotId: number;
  drivers: Array<DriverId>;
}

export interface Hazard {
  id: string;
  typeId: string;
  typeName: string;
  typeDisplayName: string;
  location: {
    longitude: number;
    latitude: number;
  };
  reportedById: string;
  reportedByName: string;
  reportedByCompany: string;
  reportedAt: string;
  resolvedAt: string;
  updatedAt: string;
  likes: number;
  dislikes: number;
  hasVoted: false;
  likeVote: null;
  comment: string;
  groupId: string;
  groupName: string;
  groupDisplayName: string;
  trucksInLine: string;
  positionInLine: string;
}

export interface MapCorners {
  corner_a: {
    longitude: number;
    latitude: number;
  };
  corner_b: {
    longitude: number;
    latitude: number;
  };
}

export interface Bonus {
  type: string;
  quantity: number;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class MigrationMapService {
  private migrationMapTargets$$ = new BehaviorSubject<MigrationMappableTarget[]>([]);
  private migrationMapTargetsThrottle$$ = new Subject();
  private migrationMapTargetsShared$ = this.migrationMapTargets$$.pipe(shareReplay(1));

  public get migrationMapTargets$(): Observable<MigrationMappableTarget[]> {
    this.migrationMapTargetsThrottle$$.next();
    return this.migrationMapTargetsShared$;
  }

  private migrationMapTargetsDismissed$$ = new BehaviorSubject<MigrationMappableTarget[]>(null);
  private migrationMapTargetsDismissedThrottle$$ = new Subject();
  private migrationMapTargetsDismissedShared$ = this.migrationMapTargetsDismissed$$.pipe(shareReplay(1));

  public get migrationMapTargetsDismissed$(): Observable<MigrationMappableTarget[]> {
    this.migrationMapTargetsDismissedThrottle$$.next();
    return this.migrationMapTargetsDismissedShared$;
  }

  private migrationMapTargetsAccepted$$ = new BehaviorSubject<MigrationMappableTargetAccepted[]>(null);
  private migrationMapTargetsAcceptedThrottle$$ = new Subject();
  private migrationMapTargetsAcceptedShared$ = this.migrationMapTargetsAccepted$$.pipe(shareReplay(1));

  public get migrationMapTargetsAccepted$(): Observable<MigrationMappableTargetAccepted[]> {
    this.migrationMapTargetsAcceptedThrottle$$.next();
    return this.migrationMapTargetsAcceptedShared$;
  }

  private migrationMapDrivers$$ = new BehaviorSubject<MigrationMappableDriver[]>([]);
  private migrationMapDriversThrottle$$ = new Subject();
  private migrationMapDriversShared$ = this.migrationMapDrivers$$.pipe(shareReplay(1));

  public get migrationMapDrivers$(): Observable<MigrationMappableDriver[]> {
    this.migrationMapDriversThrottle$$.next();
    return this.migrationMapDriversShared$;
  }

  constructor(private httpClient: HttpClient) {
    this.migrationMapTargetsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadMigrationMapTargets();
    });
    this.migrationMapDriversThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadMigrationMapDrivers();
    });
    this.migrationMapTargetsDismissedThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadMigrationMapTargetsDismissed();
    });
    this.migrationMapTargetsAcceptedThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadMigrationMapTargetsAccepted();
    });
  }

  private loadMigrationMapDrivers() {
    this.httpClient
      .get<{ drivers: MigrationMappableDriver[] }>(`${environment.api}/v2/admin/migration_map_data`)
      .subscribe((alerts) => {
        this.migrationMapDrivers$$.next(alerts.drivers || ([] as MigrationMappableDriver[]));
      });
  }

  private loadMigrationMapTargets() {
    this.httpClient
      .get<{ sandOpportunities: MigrationMappableTarget[] }>(`${environment.api}/v2/admin/opportunity`)
      .subscribe((alerts) => {
        this.migrationMapTargets$$.next(alerts.sandOpportunities || ([] as MigrationMappableTarget[]));
      });
  }

  private loadMigrationMapTargetsDismissed() {
    this.httpClient
      .get<{ sandOpportunities: MigrationMappableTarget[] }>(`${environment.api}/v2/admin/opportunity?dismissed=true`)
      .subscribe((alerts) => {
        this.migrationMapTargetsDismissed$$.next(alerts.sandOpportunities || ([] as MigrationMappableTarget[]));
      });
  }

  private loadMigrationMapTargetsAccepted() {
    this.httpClient
      .get<{ sandOpportunities: MigrationMappableTargetAccepted[] }>(`${environment.api}/v2/admin/opportunity/accepted`)
      .subscribe((alerts) => {
        this.migrationMapTargetsAccepted$$.next(alerts.sandOpportunities || ([] as MigrationMappableTargetAccepted[]));
      });
  }

  public confirmDrivers(toSend: DriversForTarget) {
    return this.httpClient.post(`${environment.api}/v2/admin/opportunity/accept_sand`, toSend).toPromise();
  }

  public dismissOpportunity(toSend: SandId) {
    return this.httpClient.post(`${environment.api}/v2/admin/opportunity/dismiss_sand`, toSend).toPromise();
  }

  public getHazards$(toSend: MapCorners): Observable<Hazard[]> {
    return this.httpClient.post<Hazard[]>(`${environment.api}/v1/hazards`, toSend);
  }

  public triggerRefresh() {
    this.migrationMapDriversThrottle$$.next();
  }
}
